import React from "react";
import { CheckCircle } from "@phosphor-icons/react";

/**
 * Shared, presentational "review before create" popup.
 *
 * Extracted from Invoice's InvoiceConfirmPopup (AgentRunModal.jsx) so PR/PO
 * agents (and any future agent) can reuse the same centered-overlay,
 * label/value-grid, Confirm/Cancel layout without duplicating markup.
 *
 * This component has zero knowledge of where `fields` come from — callers
 * are responsible for deriving them (regex-parsing a chat reply, mapping a
 * structured draft object, etc.). It only renders what it's given.
 *
 * `children` renders between the field grid and the Confirm/Cancel buttons,
 * so callers can inject extra content (e.g. a line-items block) without
 * this component needing any caller-specific knowledge.
 */
export default function ConfirmReviewPopup({
  title,
  subtitle,
  fields = [],
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  busy = false,
  children,
}) {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl p-6 w-full mx-4 shadow-2xl"
        style={{ maxWidth: 500, border: "1px solid #E5E7EB" }}>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-full bg-[#0020C2] flex items-center justify-center shrink-0">
            <CheckCircle size={18} weight="fill" color="white" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{title}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 mb-5">
          {fields.map((f, i) => (
            <div key={i}
              style={{
                gridColumn: f.highlight ? "1 / -1" : "auto",
                background: f.highlight ? "rgba(16,185,129,0.07)" : "#F9FAFB",
                border: f.highlight ? "1px solid rgba(16,185,129,0.25)" : "1px solid #E5E7EB",
                borderRadius: 10,
                padding: "9px 12px",
              }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>
                {f.label}
              </p>
              <p style={{ fontSize: f.highlight ? 15 : 13, fontWeight: f.highlight ? 700 : 500, color: f.highlight ? "#059669" : "#111827", wordBreak: "break-word" }}>
                {f.value}
              </p>
            </div>
          ))}
        </div>

        {children}

        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={busy}
            className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #10B981, #059669)", opacity: busy ? 0.7 : 1 }}>
            ✓ {confirmLabel}
          </button>
          <button onClick={onCancel} disabled={busy}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-gray-100"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#6B7280" }}>
            ✕ {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
